<template>
  <aw-drag
    v-if="edit || element.children?.length || publicUpload"
    :enable="edit && data.sort === 'rank'"
    :drag-item="element"
    #default="{ dragHandle }"
    class="DocumentBlock -gallery"
    :class="{
      DocumentHasPlaceholder: element.placeholder,
      isCommentOpened: isCommentOpened,
      DocumentHighlightSearch: $route.hash === '#el' + element.id
    }"
    :id="'el' + element.id"
  >
    <div class="GalleryShadow"></div>

    <div class="DocumentElement -gallery" ref="dropAreaEl">
      <div
        class="w-full bg-bc-blue flex flex-row justify-center space-x-2 p-4"
        v-if="element.locked && !isEveryElementLocked && !isPageLocked"
      >
        <aw-icon icon="lock" class="w-5 h-5 text-white" />
        <span class="text-white text-sm">{{ $t('Element is locked') }}</span>
      </div>

      <document-upload-drop-zone
        :upload-enabled="draggedElement === null && !dragged"
        :visible="edit || publicUpload"
        :public-upload="publicUpload"
        v-model="element"
        v-model:modelChildren="children"
        :key="element.key"
      >
        <aw-drop
          :list="staticPhotoList"
          :can-drop-item="canDropItem"
          :create-placeholder="createPlaceholder"
          transition-name="document-elements"
          list-item-key="key"
          @drop-item="dropFiles"
          :drop-area-el="$refs.dropAreaEl"
          drop-direction="x"
          class="GalleryList"
        >
          <template #default="{ item, index }">
            <document-element-gallery-file
              :key="item.key"
              :element="item"
              :parent="element"
              :page="page"
              :expanded="expandStaticPhotos"
              :diff="diff"
              :index="index"
              @expand-from-child="expand()"
              @click="openGallery(element.id, item.file ? item.file : item.file_upload)"
              @on-blur-name-input="focused = null"
              @on-focus-name-input="focused = 'sort'"
              class="GalleryList__Item"
              :class="{
                DocumentPlaceholderGalleryList: item.placeholder,
                DocumentElementDragged: item.dragged,
                'GalleryList__Item--More':
                  !edit &&
                  ((index == 7 && !expandStaticPhotos && diff > 0) ||
                    (index == totalPhotos && expandStaticPhotos))
              }"
            />
          </template>
          <template #footer>
            <div
              v-if="expandStaticPhotos && !edit"
              class="GalleryList__Item GalleryList__Item--More"
            >
              <div class="GalleryList__More" @click="collapse()">
                <span>-{{ diff + 1 }}</span>
              </div>
              <img
                src="/assets/gfx/1x1.png"
                style="width: 500px; height: auto"
                alt=""
              />
            </div>
          </template>
        </aw-drop>
      </document-upload-drop-zone>

      <document-element-gallery-modal
        v-if="expandModal"
        :images="childrenSorted"
        @collapse-gallery-modal="collapse"
      >
        <template #select>
          <action-panel-selected-items
            :element="element"
            :is-gallery-modal="true"
            :selectable-items="childrenSorted"
            :separator="false"
          />
        </template>
        <template #default>
          <div class="GalleryList w-auto max-w-full">
            <document-element-gallery-file
              v-for="(item, index) in childrenSorted"
              :key="item.key"
              :element="item"
              :parent="element"
              :page="page"
              :expanded="true"
              :diff="diff"
              :index="index"
              @click="openGallery(element.id, item.file ? item.file : item.file_upload)"
              @on-blur-name-input="focused = null"
              @on-focus-name-input="focused = 'sort'"
              class="GalleryList__Item"
              :class="{
                DocumentPlaceholderGalleryList: item.placeholder,
                DocumentElementDragged: item.dragged
              }"
            />
          </div>
        </template>
      </document-element-gallery-modal>

      <!-- {* editacni akce *} -->
      <div class="DocumentBlock__PanelLeft -gallery inline-flex items-center">
        <document-files-actions v-model="element" />
      </div>

      <!-- comments -->
      <document-comment
        v-if="bcDocumentStore.document.allow_comments"
        v-model="element.comments"
        :element="element"
        :document="bcDocumentStore.document"
        @comment-toggle="resolveZindexOnParent"
      />

      <!-- panel s moznosti noveho elementu -->
      <document-hot-action
        :parent="parent"
        :page="page"
        :index="index + 1"
        v-if="edit && !isLastOnPage"
      />
    </div>

    <div class="DocumentBlock__PanelRight -gallery">
      <span
        ref="DocumentCircle"
        class="DocumentCircle mr-2.5 sm:mr-3.5"
        :class="{
          'DocumentCircle--active':
            bcSelectedStore.selected.has(element.id) &&
            bcSelectedStore.selected.get(element.id)[3],
          'DocumentCircle--child-active':
            bcSelectedStore.selected.has(element.id) &&
            !bcSelectedStore.selected.get(element.id)[3]
        }"
        :data-item-index="element.root_rank"
        @click.prevent.stop="bcSelectedStore.select(
          element, 
          {
            isRecursive: true,
            isShift: $event.shiftKey,
            isDocument: true,
            itemIndex: element.root_rank,
          }
        )"
      ></span>
      <span
        class="cursor-move text-gray-400 hover:text-gray-500"
        v-if="edit"
        :ref="dragHandle"
      >
        <aw-icon icon="dots" class="w-4 h-4 -mx-1"></aw-icon>
        <aw-icon icon="dots" class="w-4 h-4 -mx-1"></aw-icon>
      </span>
    </div>
  </aw-drag>
</template>

<script>
import FilesElement from '../../mixins/FilesElement'
import Selected from '../../mixins/Selected'
import { useWindowSize } from '@vueuse/core'
const { width } = useWindowSize()

export default {
  mixins: [
    FilesElement,
    Selected,
  ],
  allowedChildElements: ['file'],
  dropDirection: 'horizontal',
  data() {
    return {
      expanded: false
    }
  },
  computed: {
    totalPhotos() {
      return this.expanded || this.bcDocumentStore.edit
        ? this.childrenSorted.length
        : 8
    },
    diff() {
      return this.childrenSorted.length - 8
    },
    expandModal() {
      return this.expanded && !this.edit && this.childrenSorted.length > 15 && width.value > 767
    },
    expandStaticPhotos() {
      return this.bcDocumentStore.edit || (this.expanded && this.childrenSorted.length <= 15 && width.value > 767) || (this.expanded && width.value <= 767)
    },
    staticPhotoList() {
      return this.expandStaticPhotos
        ? this.childrenSorted
        : this.childrenSorted
        ? this.childrenSorted.slice(0, 8)
        : []
    },
  },
  methods: {
    expand() {
      this.expanded = true
      if (this.expandModal) {
        document.documentElement.classList.add("lock")
        document.body.classList.add("lock")
      }
    },
    collapse() {
      if (this.expandModal) {
        document.documentElement.classList.remove("lock")
        document.body.classList.remove("lock")
      }
      this.expanded = false
    }
  },
  mounted() {
    this.assignItemToSelectBtn('DocumentCircle', this.element)
  },
}
</script>

<style lang="scss" scoped>
.GalleryList {
  @screen md {
    max-width: calc(100vw - 8.25rem);
  }
}

.GalleryList__Item {
  @screen md {
    min-width: 150px;
  }
}
</style>
